import React from 'react';

// Smooth (Catmull-Rom → Bezier) SVG path through evenly spaced values.
export const smoothPath = (vals, { min, max, w, h, pad = 10 }) => {
  if (!vals || vals.length < 2) return { d: '', lastY: h / 2 };
  const lo = min ?? Math.min(...vals);
  const hi = max ?? Math.max(...vals);
  const span = hi - lo || 1;
  const f = (n) => n.toFixed(1);
  const p = vals.map((v, i) => [i * (w / (vals.length - 1)), h - pad - ((v - lo) / span) * (h - 2 * pad)]);
  let d = `M${f(p[0][0])} ${f(p[0][1])}`;
  for (let i = 0; i < p.length - 1; i++) {
    const p0 = p[i - 1] || p[i], p1 = p[i], p2 = p[i + 1], p3 = p[i + 2] || p2;
    d += ` C${f(p1[0] + (p2[0] - p0[0]) / 6)} ${f(p1[1] + (p2[1] - p0[1]) / 6)} ${f(p2[0] - (p3[0] - p1[0]) / 6)} ${f(p2[1] - (p3[1] - p1[1]) / 6)} ${f(p2[0])} ${f(p2[1])}`;
  }
  return { d, lastY: p[p.length - 1][1] };
};

// Area/line chart. gold=true draws the glowing "north star" line.
export const WaveChart = ({ values, height = 160, gold = false, dashed = false, min, max, label, id }) => {
  const w = 640;
  const { d, lastY } = smoothPath(values, { min, max, w, h: height, pad: 14 });
  if (!d) return <div className="text-sm text-[var(--text-muted)] py-8 text-center">Za mało danych do wykresu</div>;
  const stroke = gold ? '#C9A84C' : 'var(--chart-main)';
  const gid = `g-${id || 'wave'}`;
  return (
    <svg viewBox={`0 0 ${w} ${height}`} preserveAspectRatio="none" className="w-full block overflow-visible text-[var(--text-primary)]" style={{ height }} role="img" aria-label={label}>
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={gold ? '#C9A84C' : 'currentColor'} stopOpacity={gold ? 0.28 : 0.14} />
          <stop offset="1" stopColor={gold ? '#C9A84C' : 'currentColor'} stopOpacity="0" />
        </linearGradient>
      </defs>
      {!dashed && <path d={`${d} L${w} ${height} L0 ${height} Z`} fill={`url(#${gid})`} />}
      <path d={d} fill="none" stroke={stroke} strokeWidth={gold ? 2.4 : 2} strokeDasharray={dashed ? '6 6' : undefined} className={gold ? 'gold-glow' : ''} vectorEffect="non-scaling-stroke" />
      {gold && <circle cx={w} cy={lastY} r="6" fill="var(--bg-primary)" stroke="#C9A84C" strokeWidth="2.4" vectorEffect="non-scaling-stroke" />}
    </svg>
  );
};

// Circular progress ring with centered content.
export const Ring = ({ value = 0, size = 104, stroke = 10, gold = false, children }) => {
  const C = 2 * Math.PI * 50;
  const frac = Math.max(0, Math.min(1, value));
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg viewBox="0 0 120 120" width={size} height={size} aria-hidden="true">
        <circle cx="60" cy="60" r="50" fill="none" stroke="var(--track)" strokeWidth={stroke} />
        <circle cx="60" cy="60" r="50" fill="none" stroke={gold ? '#C9A84C' : 'var(--chart-main)'} strokeWidth={stroke}
          strokeLinecap="round" strokeDasharray={`${(frac * C).toFixed(1)} ${C.toFixed(1)}`} transform="rotate(-90 60 60)"
          className={gold ? 'gold-glow' : ''} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">{children}</div>
    </div>
  );
};

export const Sparkline = ({ values, gold = false, width = 110, height = 36 }) => {
  if (!values || values.length < 2) return null;
  const lo = Math.min(...values), hi = Math.max(...values), span = hi - lo || 1;
  const pts = values.map((v, i) => `${(i * width / (values.length - 1)).toFixed(1)},${(height - 3 - ((v - lo) / span) * (height - 6)).toFixed(1)}`).join(' ');
  return (
    <svg viewBox={`0 0 ${width} ${height}`} width={width} height={height} aria-hidden="true">
      <polyline points={pts} fill="none" stroke={gold ? '#C9A84C' : 'var(--chart-main)'} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};
